import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {
  Box,
  Button,
  Flex,
  Icon,
  SearchInput,
  Space,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableHeaderRow,
  TableRow,
  Text,
  Tooltip,
  Truncate,
  useColorMode,
  useColorStyle,
} from '@tonic-one/react';
import { DeleteIcon, ErrorIcon, SuccessIcon } from '@tonic-one/react-icons';
import { t } from 'i18next';
import React, {
  ChangeEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { GlobalApi } from '@Apis';
import IconButton from '../../../../components/IconButton';
import { NotificationMailboxesResDto } from '../../../../types/globalSetting';
import AddModal from './AddModal';
import { useDebounceState } from '@Utils/hooks';
import DeleteModal from './DeleteModal';
import { usePermissionStore } from '../../../../models/usePermissionModel';
import { PermissionNameEnum } from '@Types/permissions';

enum ModalType {
  None = 'none',
  Add = 'add',
  Delete = 'delete',
}

type ModalState =
  | {
      type: ModalType.Add | ModalType.None;
      data: null;
    }
  | {
      type: ModalType.Delete;
      data: {
        mailbox: string;
      };
    };

const SendingMailboxes = () => {
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  const { widthEditablePermission } = usePermissionStore();
  const adminEditPermission = widthEditablePermission(
    PermissionNameEnum.administrator
  );
  const [mailboxInfos, setMailboxInfos] = useState<
    NotificationMailboxesResDto[] | []
  >([]);
  const fetchData = useCallback(async () => {
    try {
      const data: NotificationMailboxesResDto[] = (
        await GlobalApi.getNotificationMailboxes()
      ).data;
      setMailboxInfos(data);
    } catch {
      setMailboxInfos([]);
    }
  }, []);

  const [modalState, setModalState] = useState<ModalState>({
    type: ModalType.None,
    data: null,
  });

  const onModalClose = useCallback(
    (needRefresh: boolean) => {
      if (needRefresh) {
        fetchData();
      }
      setModalState({ type: ModalType.None, data: null });
    },
    [fetchData]
  );

  const [keyword, setKeyword] = useDebounceState('');

  const filteredMailboxInfos = useMemo(() => {
    return keyword === ''
      ? mailboxInfos
      : mailboxInfos.filter((item) =>
          item.mailbox.toLowerCase().includes(keyword)
        );
  }, [keyword, mailboxInfos]);

  const hoverBackgroundColor = {
    dark: 'rgba(255, 255, 255, 0.12)',
    light: 'rgba(0, 0, 0, 0.12)',
  }[colorMode];

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const columns: ColumnDef<NotificationMailboxesResDto>[] = useMemo(
    () => [
      {
        id: 'mailbox',
        header: t('globalSetting.sendingMailboxes.column.emailAddress'),
        accessorKey: 'mailbox',
        size: 700,
        style: {
          textAlign: 'right',
        },
      },
      {
        id: 'tokenStatus',
        header: t('globalSetting.sendingMailboxes.column.status'),
        cell: ({ row }) => {
          if (row.original.tokenStatus === 1) {
            return (
              <Stack direction="row">
                <Icon as={SuccessIcon} color={colorStyle.color.success}></Icon>
              </Stack>
            );
          } else {
            return (
              <Stack direction="row">
                <Icon as={ErrorIcon} color={colorStyle.color.error}></Icon>
              </Stack>
            );
          }
        },
        size: 200,
        style: {
          textAlign: 'center',
        },
      },
      {
        id: 'actions',
        header: t('policy.table.fields.actions'),
        cell: ({ row }) => {
          return (
            <IconButton
              onClick={() => {
                setModalState({
                  type: ModalType.Delete,
                  data: {
                    mailbox: row.original.mailbox,
                  },
                });
              }}
              disabled={!adminEditPermission}
            >
              <Tooltip label={t('policy.tooltip.delete')}>
                <Icon as={DeleteIcon} />
              </Tooltip>
            </IconButton>
          );
        },
        size: 100,
      },
    ],
    [colorStyle, adminEditPermission]
  );
  const table = useReactTable({
    data: filteredMailboxInfos,
    columns,
    defaultColumn: {
      minSize: 40,
    },
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => row.mailbox,
  });

  return (
    <>
      <Box>
        <Flex m="16px 0px">
          <Text color={colorStyle.color.secondary}>
            {t('globalSetting.sendingMailboxes.description')}
          </Text>
        </Flex>
        <Flex m="16px 0px">
          <Text>
            <Space>{t('globalSetting.note.title')}</Space>
            <Space width="1x"></Space>
            <Space color={colorStyle.color.secondary}>
              {t('globalSetting.note.applyToAllOrganizations')}
            </Space>
          </Text>
        </Flex>
        <Flex alignItems="center">
          <Button
            variant="primary"
            onClick={() => {
              setModalState({
                type: ModalType.Add,
                data: null,
              });
            }}
            disabled={!adminEditPermission}
          >
            {t('common:common.action.label.add')}
          </Button>
          <Space width="12px" />
          <SearchInput
            placeholder={t(
              'globalSetting.sendingMailboxes.searchInput.placeholder'
            )}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              setKeyword(e.target.value.toLowerCase());
            }}
            onClearInput={() => setKeyword('')}
            width="374px"
          />
        </Flex>

        <Table mt={28}>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableHeaderRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const styleProps = {
                    minWidth: header.column.columnDef.minSize,
                    width: header.getSize(),
                  };
                  return (
                    <TableHeaderCell key={header.id} {...styleProps}>
                      {header.isPlaceholder ? null : (
                        <Truncate>
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                        </Truncate>
                      )}
                    </TableHeaderCell>
                  );
                })}
              </TableHeaderRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows.map((row) => (
              <TableRow
                key={row.id}
                _hover={{
                  backgroundColor: hoverBackgroundColor,
                }}
              >
                {row.getVisibleCells().map((cell) => {
                  const styleProps = {
                    minWidth: cell.column.columnDef.minSize,
                    width: cell.column.getSize(),
                  };
                  return (
                    <TableCell key={cell.id} {...styleProps}>
                      <Truncate>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </Truncate>
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>

      {modalState.type === ModalType.Add && (
        <AddModal onModalClose={onModalClose} />
      )}

      {modalState.type === ModalType.Delete && (
        <DeleteModal
          mailbox={
            modalState.type === ModalType.Delete ? modalState.data.mailbox : ''
          }
          onModalClose={onModalClose}
        />
      )}
    </>
  );
};
export default SendingMailboxes;
